/**
 * Lightbox Component
 *
 * Full-screen image viewer with navigation, keyboard support,
 * and touch/swipe gestures for mobile.
 */

export interface LightboxImage {
  src: string;
  alt?: string;
  caption?: string;
}

export interface LightboxOptions {
  images: LightboxImage[];
  startIndex?: number;
  onClose?: () => void;
  /** Root element to append lightbox to (for shadow DOM support) */
  rootElement?: ShadowRoot | HTMLElement | null;
}

// Singleton instance
let activeLightbox: Lightbox | null = null;

/**
 * Open the lightbox with given images
 */
export function openLightbox(options: LightboxOptions): Lightbox {
  // Close existing lightbox if open
  if (activeLightbox) {
    activeLightbox.close();
  }

  activeLightbox = new Lightbox(options);
  activeLightbox.open();
  return activeLightbox;
}

/**
 * Close the active lightbox
 */
export function closeLightbox(): void {
  if (activeLightbox) {
    activeLightbox.close();
    activeLightbox = null;
  }
}

/**
 * Lightbox class for displaying full-screen images
 */
export class Lightbox {
  private overlay: HTMLElement | null = null;
  private currentIndex: number;
  private images: LightboxImage[];
  private onCloseCallback?: () => void;
  private rootElement: ShadowRoot | HTMLElement;

  // Touch handling
  private touchStartX: number = 0;
  private touchEndX: number = 0;

  // Bound event handlers for cleanup
  private boundKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  private boundTouchStart: ((e: TouchEvent) => void) | null = null;
  private boundTouchEnd: ((e: TouchEvent) => void) | null = null;

  constructor(options: LightboxOptions) {
    this.images = options.images;
    this.currentIndex = options.startIndex ?? 0;
    this.onCloseCallback = options.onClose;
    // Use provided root element, or fall back to document.body
    this.rootElement = options.rootElement || document.body;
  }

  /**
   * Open the lightbox
   */
  open(): void {
    if (this.images.length === 0) return;

    this.render();
    this.bindKeyboard();
    this.bindSwipe();

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Animate in
    requestAnimationFrame(() => {
      this.overlay?.classList.add('pms-lightbox-open');
    });
  }

  /**
   * Close the lightbox
   */
  close(): void {
    if (!this.overlay) return;

    // Animate out
    this.overlay.classList.remove('pms-lightbox-open');

    // Remove after animation
    setTimeout(() => {
      this.cleanup();
    }, 300);

    // Restore body scroll
    document.body.style.overflow = '';

    this.onCloseCallback?.();
  }

  /**
   * Navigate to next image
   */
  next(): void {
    if (this.currentIndex < this.images.length - 1) {
      this.currentIndex++;
      this.updateImage();
    }
  }

  /**
   * Navigate to previous image
   */
  prev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateImage();
    }
  }

  /**
   * Go to specific image index
   */
  goTo(index: number): void {
    if (index >= 0 && index < this.images.length) {
      this.currentIndex = index;
      this.updateImage();
    }
  }

  /**
   * Render the lightbox DOM
   */
  private render(): void {
    const image = this.images[this.currentIndex];
    if (!image) return;

    const hasMultiple = this.images.length > 1;

    this.overlay = document.createElement('div');
    this.overlay.className = 'pms-lightbox-overlay';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', 'Image lightbox');

    this.overlay.innerHTML = `
      <div class="pms-lightbox-container">
        <button class="pms-lightbox-close" aria-label="Close lightbox">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        ${hasMultiple ? `
          <button class="pms-lightbox-nav pms-lightbox-prev" aria-label="Previous image" ${this.currentIndex === 0 ? 'disabled' : ''}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
        ` : ''}

        <div class="pms-lightbox-content">
          <img
            class="pms-lightbox-image"
            src="${image.src}"
            alt="${image.alt || image.caption || 'Image'}"
          />
          ${image.caption ? `<div class="pms-lightbox-caption">${image.caption}</div>` : ''}
        </div>

        ${hasMultiple ? `
          <button class="pms-lightbox-nav pms-lightbox-next" aria-label="Next image" ${this.currentIndex === this.images.length - 1 ? 'disabled' : ''}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        ` : ''}

        ${hasMultiple ? `
          <div class="pms-lightbox-counter">
            ${this.currentIndex + 1} / ${this.images.length}
          </div>
        ` : ''}
      </div>
    `;

    // Event listeners
    const closeBtn = this.overlay.querySelector('.pms-lightbox-close');
    closeBtn?.addEventListener('click', () => this.close());

    const prevBtn = this.overlay.querySelector('.pms-lightbox-prev');
    prevBtn?.addEventListener('click', () => this.prev());

    const nextBtn = this.overlay.querySelector('.pms-lightbox-next');
    nextBtn?.addEventListener('click', () => this.next());

    // Close on overlay click (not on content)
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    this.rootElement.appendChild(this.overlay);

    // Focus trap - focus the close button
    (closeBtn as HTMLElement)?.focus();
  }

  /**
   * Update the displayed image
   */
  private updateImage(): void {
    if (!this.overlay) return;

    const image = this.images[this.currentIndex];
    if (!image) return;

    const hasMultiple = this.images.length > 1;

    const imgEl = this.overlay.querySelector('.pms-lightbox-image') as HTMLImageElement;
    if (imgEl) {
      imgEl.src = image.src;
      imgEl.alt = image.alt || image.caption || 'Image';
    }

    const captionEl = this.overlay.querySelector('.pms-lightbox-caption');
    if (captionEl) {
      captionEl.textContent = image.caption || '';
      (captionEl as HTMLElement).style.display = image.caption ? 'block' : 'none';
    } else if (image.caption) {
      // Add caption if it didn't exist before
      const contentEl = this.overlay.querySelector('.pms-lightbox-content');
      const newCaption = document.createElement('div');
      newCaption.className = 'pms-lightbox-caption';
      newCaption.textContent = image.caption;
      contentEl?.appendChild(newCaption);
    }

    const counterEl = this.overlay.querySelector('.pms-lightbox-counter');
    if (counterEl) {
      counterEl.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
    }

    // Update nav button states
    if (hasMultiple) {
      const prevBtn = this.overlay.querySelector('.pms-lightbox-prev') as HTMLButtonElement;
      const nextBtn = this.overlay.querySelector('.pms-lightbox-next') as HTMLButtonElement;

      if (prevBtn) prevBtn.disabled = this.currentIndex === 0;
      if (nextBtn) nextBtn.disabled = this.currentIndex === this.images.length - 1;
    }
  }

  /**
   * Bind keyboard navigation
   */
  private bindKeyboard(): void {
    this.boundKeyHandler = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          this.prev();
          break;
        case 'ArrowRight':
          this.next();
          break;
      }
    };

    document.addEventListener('keydown', this.boundKeyHandler);
  }

  /**
   * Bind touch/swipe gestures
   */
  private bindSwipe(): void {
    this.boundTouchStart = (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      if (touch) this.touchStartX = touch.screenX;
    };

    this.boundTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      if (touch) this.touchEndX = touch.screenX;
      this.handleSwipe();
    };

    this.overlay?.addEventListener('touchstart', this.boundTouchStart, { passive: true });
    this.overlay?.addEventListener('touchend', this.boundTouchEnd, { passive: true });
  }

  /**
   * Handle swipe gesture
   */
  private handleSwipe(): void {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swipe left - next image
        this.next();
      } else {
        // Swipe right - previous image
        this.prev();
      }
    }
  }

  /**
   * Clean up DOM and event listeners
   */
  private cleanup(): void {
    if (this.boundKeyHandler) {
      document.removeEventListener('keydown', this.boundKeyHandler);
      this.boundKeyHandler = null;
    }

    if (this.boundTouchStart && this.overlay) {
      this.overlay.removeEventListener('touchstart', this.boundTouchStart);
      this.boundTouchStart = null;
    }

    if (this.boundTouchEnd && this.overlay) {
      this.overlay.removeEventListener('touchend', this.boundTouchEnd);
      this.boundTouchEnd = null;
    }

    this.overlay?.remove();
    this.overlay = null;

    if (activeLightbox === this) {
      activeLightbox = null;
    }
  }
}
